import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { AccidentType } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { AccidentTypeForm } from "../AccidentTypeForm/AccidentTypeForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListAccidentTypes.scss";

const accidentTypeController = new AccidentType();

export function ListAccidentTypes({ accidentTypesActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [accidentTypes,       setAccidentTypes]       = useState(null);
  const [accidentTypesFilter, setAccidentTypesFilter] = useState([]);
  const [search,              setSearch]              = useState("");
  const [showModal,           setShowModal]           = useState(false);
  const [selectedItem,        setSelectedItem]        = useState(null);
  const [showConfirm,         setShowConfirm]         = useState(false);
  const [confirmMsg,          setConfirmMsg]          = useState("");
  const [isDelete,            setIsDelete]            = useState(false);
  const [rowMenu,             setRowMenu]             = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".accident-types-list__row-menu, .accident-types-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setAccidentTypes(null);
        const res = await accidentTypeController.getAccidentTypes(accessToken, accidentTypesActive);
        setAccidentTypes(res);
        setAccidentTypesFilter(res);
      } catch {
        setAccidentTypes([]);
        setAccidentTypesFilter([]);
      }
    })();
  }, [accidentTypesActive, reload]);

  useEffect(() => {
    if (!accidentTypes) return;
    if (!search) { setAccidentTypesFilter(accidentTypes); return; }
    const q = search.toLowerCase();
    setAccidentTypesFilter(
      accidentTypes.filter((a) =>
        a.code?.toLowerCase().includes(q) ||
        a.name?.toLowerCase().includes(q) ||
        a.country?.toLowerCase().includes(q)
      )
    );
  }, [search, accidentTypes]);

  const openCreate = () => { setSelectedItem(null); setShowModal(true); };
  const openEdit   = (a)  => { setSelectedItem(a);   setShowModal(true); };

  const openToggle = (a) => {
    setSelectedItem(a);
    setIsDelete(false);
    setConfirmMsg(`¿${a.active ? t("deactivate") : t("activate")} "${a.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (a) => {
    setSelectedItem(a);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${a.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await accidentTypeController.updateAccidentType(accessToken, selectedItem._id, { active: !selectedItem.active });
      toast.success(selectedItem.active ? t("accident_type_deactivated") : t("accident_type_activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await accidentTypeController.deleteAccidentType(accessToken, selectedItem._id);
      toast.success(t("accident_type_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (accidentTypes === null) return <LoadingSpinner />;

  return (
    <div className="accident-types-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="accident-types-list__card">
        <div className="accident-types-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-accident-type-btn" title={t("create")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {accidentTypesFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="accident-types-list__tbl-scroll">
            <Table className="accident-types-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell>País</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {accidentTypesFilter.map((a) => (
                  <Table.Row key={a._id}>
                    <Table.Cell data-label={t("code")}>{a.code ? a.code.charAt(0).toUpperCase() + a.code.slice(1).toLowerCase() : ""}</Table.Cell>
                    <Table.Cell data-label={t("name")}>{a.name}</Table.Cell>
                    <Table.Cell data-label="País">{a.country ? a.country.charAt(0).toUpperCase() + a.country.slice(1) : <span style={{ color: "#aaa" }}>Todos</span>}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="accident-types-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="accident-types-list__row-menu">
                        <button type="button" className="accident-types-list__row-menu-btn" onClick={(e) => toggleRowMenu(a._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === a._id && ReactDOM.createPortal(
                        <div
                          className="accident-types-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(a); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(a); setRowMenu(null); }}>
                            <Icon name={a.active ? "ban" : "check"} />{a.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="accident-types-list__row-menu-item--danger" onClick={() => { openDelete(a); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedItem ? `${t("edit")}: ${selectedItem.name}` : t("new_accident_type")}
        size="small"
        className="catalog-modal"
      >
        <AccidentTypeForm
          accidentType={selectedItem}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selectedItem?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
